import classnames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { DiscountAmount, discountForDisplay, Discounts, freeShippingThresholdInDollars, getActiveDiscount, isInDateRange } from '../discounts';
import { useReturnsModal } from '../hooks/actions/useReturnsModal';
import { Icon } from '../icons/Icon';
import { Shopify } from '../models/Shopify.model';
import { abTestState, appliedDiscountCodeState, environmentContextState, shopifyState, toViewPriceFnState } from '../recoil/atoms';
import { DivButton } from '../root/components/DivButton';
import { bannerCookieName, getCookie } from '../util';
import styles from './BannerApp.module.scss';

export const BannerApp: React.FC = () => {
  const launchReturnsModal = useReturnsModal();
  const environmentContext = useRecoilValue(environmentContextState);
  const appliedDiscountCode = useRecoilValue(appliedDiscountCodeState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const { timedSaleActive } = useRecoilValue(environmentContextState);
  const activeDiscount = getActiveDiscount(2, timedSaleActive, abTests);

  const [bannerIndex, setBannerIndex] = useState<number>(0);
  const [fadeInIndex, setFadeInIndex] = useState<number>(-1);
  const [messages, setMessages] = useState<BannerMessage[]>([]);
  const displayDiscount = discountForDisplay(2, toViewPriceFn, timedSaleActive, shopify, abTests);

  const cookieBanner = getCookie(bannerCookieName);
  const parsedCookieMessages = () => {
    const parsedMarketingBanner = cookieBanner && JSON.parse(cookieBanner);
    return parsedMarketingBanner ? parsedMarketingBanner.map((message: string) => ({ message, context: MessageContext.CookieMessage })) : [];
  };

  const contextIsActive = contextIsActiveFn(shopify, appliedDiscountCode, activeDiscount);
  const messageFilterFn = (bannerMessage: BannerMessage) => contextIsActive(bannerMessage);
  const previousMessage = () => {
    setBannerIndex(bannerIndex - 1 >= 0 ? bannerIndex - 1 : messages.length - 1);
  };
  const nextMessage = () => {
    setBannerIndex(bannerIndex + 1 < messages.length ? bannerIndex + 1 : 0);
  };

  const messageKey = JSON.stringify(messages);
  useEffect(() => {
    const interval = setTimeout(() => {
      nextMessage();
    }, 6000);
    return () => clearTimeout(interval);
  }, [bannerIndex, messageKey]);

  useEffect(() => {
    const interval = setTimeout(() => {
      setFadeInIndex(bannerIndex);
    }, 1000);
    return () => clearTimeout(interval);
  }, [bannerIndex]);

  useEffect(() => {
    const defaultMessages: BannerMessage[] = [
      {
        message: 'ANNIVERSARY SALE: Up to 50% off!',
        contexts: [MessageContext.TimedPromo],
        startDate: environmentContext.timedSaleStartISO,
        endDate: '2022-07-12T07:00:00.000Z',
      },
      {
        message: 'SALE LAST CHANCE: Up to 50% off until 7/13',
        contexts: [MessageContext.TimedPromo],
        startDate: '2022-07-12T07:00:01.000Z',
        endDate: environmentContext.timedSaleStopISO,
      },
      {
        message: `Weekend Only: Earn $${environmentContext.loyaltyFlashSaleMultiplier * 10} for every $100 spent.`,
        contexts: [MessageContext.US, MessageContext.FlaggedPromo],
        flagIsActive: isInDateRange(environmentContext.loyaltyFlashSaleStartISO, environmentContext.loyaltyFlashSaleStopISO),
        onClick: () => (window.location.href = '/pages/loyalty'),
      },
      { message: `Discount code applied!`, contexts: MessageContext.DiscountCode },
      {
        message: `Buy one item, take ${displayDiscount} off the rest of your order`,
        contexts: MessageContext.BundleActive,
      },
      {
        message: `Free shipping on orders over ${toViewPriceFn(freeShippingThresholdInDollars, 0)}`,
        contexts: [MessageContext.US, MessageContext.NoDiscountCode],
      },
      { message: 'Free 30-day returns', contexts: MessageContext.US, onClick: () => launchReturnsModal() },
      { message: 'All orders come with a 30-day fit guarantee', contexts: MessageContext.International, onClick: () => launchReturnsModal() },
    ];
    const filteredMessages = defaultMessages.filter(messageFilterFn);
    const cookieMessages = parsedCookieMessages();
    const activeMessages = [...cookieMessages, ...filteredMessages];
    setBannerIndex(0);
    setMessages(activeMessages);
  }, [appliedDiscountCode]);

  const showArrows = messages.length > 1;
  const displayMessage = messages && messages[bannerIndex];

  return (
    <div className={styles.bannerAppRoot}>
      {showArrows && (
        <DivButton onClick={previousMessage} label="Show previous message">
          <Icon name="chevron-left" style={{ width: 7, marginBottom: -2 }} />
        </DivButton>
      )}
      {displayMessage && (
        <DivButton
          onClick={displayMessage.onClick ? displayMessage.onClick : console.log}
          className={classnames(styles.message, { [styles.fadeIn]: fadeInIndex !== bannerIndex })}
          style={displayMessage.onClick ? {} : { cursor: 'default' }}
          label="View more info"
        >
          {displayMessage.message}
          {!!displayMessage.onClick && <Icon name="info" style={{ height: '20px', width: '20px', color: 'black', position: 'absolute', marginLeft: '5px' }} />}
        </DivButton>
      )}
      {showArrows && (
        <DivButton onClick={nextMessage} label="Show next message">
          <Icon name="chevron-right" style={{ width: 7, marginBottom: -2 }} />
        </DivButton>
      )}
    </div>
  );
};

enum MessageContext {
  US = 'US',
  International = 'INTERNATIONAL',
  BundleActive = 'BUNDLE_ACTIVE',
  CookieMessage = 'COOKIE_MESSAGE',
  TimedPromo = 'TIMED_PROMO',
  Any = 'ANY',
  DiscountCode = 'DISCOUNT_CODE',
  NoDiscountCode = 'NO_DISCOUNT_CODE',
  FlaggedPromo = 'FLAGGED_PROMO',
}
interface BannerMessage {
  message: string;
  contexts: MessageContext | MessageContext[];
  onClick?: () => void;
  startDate?: string;
  endDate?: string;
  flagIsActive?: boolean;
}

const contextIsActiveFn =
  (shopify: Shopify | undefined, appliedDiscountCode: string | undefined, activeDiscount: DiscountAmount) =>
  ({ contexts, startDate, endDate, flagIsActive }: BannerMessage) => {
    if (Array.isArray(contexts)) {
      for (const c of contexts) {
        if (!checkContext(c, startDate, endDate, flagIsActive, shopify, appliedDiscountCode, activeDiscount)) {
          return false;
        }
      }
      return true;
    } else {
      return checkContext(contexts, startDate, endDate, flagIsActive, shopify, appliedDiscountCode, activeDiscount);
    }
  };

const checkContext = (
  context: MessageContext,
  startDate: string | undefined,
  endDate: string | undefined,
  flagIsActive: boolean | undefined,
  shopify: Shopify | undefined,
  appliedDiscountCode: string | undefined,
  activeDiscount: DiscountAmount,
): boolean => {
  switch (context) {
    case MessageContext.US:
      return shopify?.currency.active === 'USD';
    case MessageContext.DiscountCode:
      return !!appliedDiscountCode;
    case MessageContext.NoDiscountCode:
      return !appliedDiscountCode;
    case MessageContext.International:
      return !!shopify && shopify.currency.active !== 'USD';
    case MessageContext.BundleActive:
      return activeDiscount.name === Discounts.bundle20Percent;
    case MessageContext.TimedPromo:
      return !!startDate && !!endDate && dayjs().isAfter(dayjs(startDate)) && dayjs().isBefore(dayjs(endDate));
    case MessageContext.FlaggedPromo:
      return !!flagIsActive;
    default:
      return true;
  }
};

export default BannerApp;
